'use client'

import { useTranslations } from 'next-intl'

import { Chip } from '@/components/ui/chip'

import { cn, isFieldInvalid } from '@/utils'

import { FieldWrapper, FieldWrapperClasses } from './field-wrapper'
import { useFieldContext } from './form'

interface DaysOfWeekSelectorProps {
  className?: string
  classNames?: FieldWrapperClasses
  label?: string
  required?: boolean
}

export const DaysOfWeekSelector = ({
  className,
  classNames,
  label,
  required = false
}: DaysOfWeekSelectorProps) => {
  const t = useTranslations('components.form.daysOfWeek')
  const field = useFieldContext<string[]>()
  const isInvalid = isFieldInvalid(field)

  const routineDays = [
    { label: t('sun'), value: '1' },
    { label: t('mon'), value: '2' },
    { label: t('tue'), value: '3' },
    { label: t('wed'), value: '4' },
    { label: t('thu'), value: '5' },
    { label: t('fri'), value: '6' },
    { label: t('sat'), value: '7' }
  ]

  const selectedDays = (field.state.value as string[]) ?? []

  const toggleDay = (dayValue: string) => {
    const currentDays = [...selectedDays]
    const dayIndex = currentDays.indexOf(dayValue)

    if (dayIndex > -1) {
      currentDays.splice(dayIndex, 1)
    } else {
      currentDays.push(dayValue)
    }

    field.handleChange(currentDays)
  }

  return (
    <FieldWrapper
      classNames={{
        ...classNames,
        wrapper: cn(className, classNames?.wrapper)
      }}
      field={field}
      label={label}
      required={required}
    >
      <div
        className="flex flex-wrap gap-2"
        data-invalid={isInvalid || undefined}
        role="group"
      >
        {routineDays.map((day) => {
          const isSelected = selectedDays.includes(day.value)

          return (
            <Chip
              aria-pressed={isSelected}
              capitalize={false}
              className={cn(
                'min-w-[2.75rem] cursor-pointer justify-center px-3.5 py-1.5 text-sm font-medium transition-all',
                'focus-visible:ring-primary/40 shadow-none outline-none focus-visible:ring-2 focus-visible:ring-offset-0',
                isSelected
                  ? 'border-primary bg-primary/10 text-primary hover:bg-primary/15'
                  : 'border-input bg-background text-muted-foreground hover:border-primary/40 hover:text-foreground'
              )}
              key={day.value}
              onClick={() => toggleDay(day.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault()
                  toggleDay(day.value)
                }
              }}
              role="button"
              tabIndex={0}
              variant="outline"
            >
              {day.label}
            </Chip>
          )
        })}
      </div>
    </FieldWrapper>
  )
}
